import { useState } from 'react'
import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router'
import { type ColumnDef, type OnChangeFn, type SortingState } from '@tanstack/react-table'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DataTable, SortableHeader } from '@/components/ui/data-table'
import { EmptyState } from '@/components/ui/empty-state'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { CalendarClock, Plus } from 'lucide-react'
import { PageHeaderActions } from '@/components/layout/page-header'
import { DealCreateForm } from '@/components/pipeline/deal-create-form'
import { ImportDealsCta } from '@/components/pipeline/import-deals-cta'
import { StageTransitionControl } from '@/components/pipeline/stage-transition'
import { PipelineBoard } from '@/components/pipeline/board-view'
import { PipelineFunnel } from '@/components/pipeline/funnel-view'
import { usePipelineList } from '@/hooks/use-pipeline'
import { cn } from '@/lib/utils'
import { DEFAULT_PAGE_SIZE, pageCount } from '@/lib/list-query'
import {
  EXPIRING_SOON_DAYS,
  STAGE_CONFIG,
  STAGE_ORDER,
  daysUntil,
  formatCurrency,
  localDateISO,
  type PipelineStage,
} from '@/lib/pipeline'
import { PIPELINE_VIEWS } from './_authed.pipeline'

type PipelineView = (typeof PIPELINE_VIEWS)[number]

export const Route = createFileRoute('/_authed/pipeline/')({
  component: PipelinePage,
})

type PipelineRow = NonNullable<ReturnType<typeof usePipelineList>['data']>['rows'][number]

const VIEW_LABELS: Record<PipelineView, string> = {
  list: 'List',
  board: 'Board',
  funnel: 'Funnel',
}

function fmtDate(dateStr: string | null): string {
  if (!dateStr) return '—'
  return new Date(dateStr + 'T00:00:00').toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })
}

function PipelinePage() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = useSearch({ from: '/_authed/pipeline' })
  const [createOpen, setCreateOpen] = useState(false)

  return (
    <div className="flex flex-col h-full">
      {/* Slice 1B-1: actions live in the ONE shared header via the portal
          slot — no route-owned header bar, title, ThemeToggle, or UserMenu. */}
      <PageHeaderActions>
        <div className="flex shrink-0 items-center rounded-md border border-[color:var(--line)] p-0.5">
          {PIPELINE_VIEWS.map((view) => (
            <Button
              key={view}
              variant="ghost"
              size="sm"
              className={cn(
                'h-6 rounded-[5px] px-2.5 text-[12px]',
                search.view === view
                  ? 'bg-[color:var(--surface-2)] text-[color:var(--ink)]'
                  : 'text-[color:var(--ink-3)]',
              )}
              onClick={() => void navigate({ search: (prev) => ({ ...prev, view }) })}
            >
              {VIEW_LABELS[view]}
            </Button>
          ))}
        </div>
        <Button
          size="sm"
          className="ml-2 h-7 gap-1.5 px-2.5 text-[12px]"
          onClick={() => setCreateOpen(true)}
        >
          <Plus className="h-3.5 w-3.5" />
          New deal
        </Button>
      </PageHeaderActions>
      <DealCreateForm open={createOpen} onOpenChange={setCreateOpen} />

      {search.view === 'list' ? (
        <PipelineListView />
      ) : (
        <div className="flex-1 overflow-auto">
          <div className="px-6 pt-4 pb-6 h-full">
            {search.view === 'board' ? <PipelineBoard /> : <PipelineFunnel />}
          </div>
        </div>
      )}
    </div>
  )
}

function PipelineListView() {
  const navigate = useNavigate({ from: Route.fullPath })
  const search = useSearch({ from: '/_authed/pipeline' })
  const [pageIndex, setPageIndex] = useState(0)

  // Any filter/sort change invalidates the current page position; reset
  // during render (React's "adjusting state when props change" pattern).
  const filterKey = JSON.stringify([search.stage, search.sort_by, search.sort_dir])
  const [prevFilterKey, setPrevFilterKey] = useState(filterKey)
  if (filterKey !== prevFilterKey) {
    setPrevFilterKey(filterKey)
    setPageIndex(0)
  }

  // Filters, sort, and paging all apply server-side.
  const { data, isLoading } = usePipelineList({
    pageIndex,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: search.sort_by,
    sortDir: search.sort_dir,
    stages: search.stage ? [search.stage] : undefined,
  })
  const deals = data?.rows ?? []
  const total = data?.total ?? 0

  const sorting: SortingState = [{ id: search.sort_by, desc: search.sort_dir === 'desc' }]
  const handleSortingChange: OnChangeFn<SortingState> = (updater) => {
    const next = typeof updater === 'function' ? updater(sorting) : updater
    void navigate({
      search: (prev) => ({
        ...prev,
        sort_by: next[0]?.id ?? 'updated_at',
        sort_dir: next[0]?.desc ? ('desc' as const) : ('asc' as const),
      }),
    })
  }

  // Contract column set/order: Stage, End-User, Reseller, Quote #, Total,
  // Target close, Expiration, Rep, Last activity — all server-sortable.
  const columns: ColumnDef<PipelineRow>[] = [
    {
      accessorKey: 'stage',
      header: ({ column }) => <SortableHeader label="Stage" column={column} />,
      cell: ({ row }) => (
        <StageTransitionControl id={row.original.id} stage={row.original.stage} />
      ),
    },
    {
      id: 'end_user_name',
      accessorFn: (row) => row.end_user?.name ?? '',
      header: ({ column }) => <SortableHeader label="End-user" column={column} />,
      cell: ({ row }) => (
        <span className="font-medium block truncate max-w-[180px]">
          {row.original.end_user?.name ?? '—'}
        </span>
      ),
    },
    {
      id: 'reseller_name',
      accessorFn: (row) => row.reseller?.name ?? '',
      header: ({ column }) => <SortableHeader label="Reseller" column={column} />,
      cell: ({ row }) => (
        <span className="block truncate max-w-[180px]">
          {row.original.reseller?.name ?? '—'}
        </span>
      ),
    },
    {
      accessorKey: 'quote_ref',
      header: ({ column }) => <SortableHeader label="Quote #" column={column} />,
      cell: ({ row }) => (
        // §1 product signature: identifiers wear the mono face at 0.92em of
        // their context — "the one choice a user should remember the product
        // by". The detail page already did this; the list did not, so the same
        // value wore two faces one click apart.
        <span className="font-mono text-[0.92em] whitespace-nowrap tabular-nums [font-feature-settings:'tnum'_1]">
          {row.original.quote_ref?.trim() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'total_amount',
      header: ({ column }) => (
        <div className="flex justify-end">
          <SortableHeader label="Total" column={column} />
        </div>
      ),
      cell: ({ row }) => (
        <div className="text-right tabular-nums [font-feature-settings:'tnum'_1]">
          {formatCurrency(row.original.total_amount)}
        </div>
      ),
    },
    {
      accessorKey: 'target_close_date',
      header: ({ column }) => <SortableHeader label="Target close" column={column} />,
      cell: ({ row }) => (
        // §4: dates in tables set tabular figures. Without tnum they computed
        // `font-variant-numeric: normal`, so a date column could not align.
        <span className="text-[13px] whitespace-nowrap tabular-nums">
          {fmtDate(row.original.target_close_date)}
        </span>
      ),
    },
    {
      accessorKey: 'expiration_date',
      header: ({ column }) => <SortableHeader label="Expiration" column={column} />,
      cell: ({ row }) => {
        const date = row.original.expiration_date
        if (!date) return <span className="text-[13px] tabular-nums">—</span>
        const expiresIn = daysUntil(date, localDateISO(new Date()))
        return (
          <span
            className={cn(
              'inline-flex items-center gap-1 text-[13px] whitespace-nowrap tabular-nums',
              // --status-lost-fg, not --destructive: the latter is a shadcn
              // compat token with no DESIGN.md definition (§12 requires every
              // colour to trace to one).
              expiresIn < EXPIRING_SOON_DAYS &&
                'text-[color:var(--status-lost-fg)] font-medium',
            )}
            // §11 banned pattern: status conveyed by colour without a label.
            // Colour was the SOLE carrier of "expiring soon" here — no icon, no
            // title, no text — while the board card already carried both.
            title={
              expiresIn < EXPIRING_SOON_DAYS
                ? expiresIn < 0
                  ? `Expired ${-expiresIn} day${expiresIn === -1 ? '' : 's'} ago`
                  : `Expires in ${expiresIn} day${expiresIn === 1 ? '' : 's'}`
                : undefined
            }
          >
            {expiresIn < EXPIRING_SOON_DAYS && (
              <CalendarClock className="h-3 w-3 shrink-0" aria-hidden="true" />
            )}
            {fmtDate(date)}
          </span>
        )
      },
    },
    {
      id: 'rep_name',
      accessorFn: (row) => row.rep?.full_name ?? '',
      header: ({ column }) => <SortableHeader label="Rep" column={column} />,
      cell: ({ row }) => (
        <span className="block truncate max-w-[140px] text-[13px]">
          {row.original.rep?.full_name?.trim() || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'updated_at',
      header: ({ column }) => <SortableHeader label="Last activity" column={column} />,
      cell: ({ row }) => (
        // §4 covers "dates in tables" — this is the third date column and it
        // was missed by the first pass; the browser evidence caught it, the
        // source-level plant did not, because the plant named only the two
        // columns I already knew about.
        <span
          className="text-[12px] text-[color:var(--ink-3)] whitespace-nowrap tabular-nums"
          title={fmtDate(row.original.updated_at.slice(0, 10))}
        >
          {new Date(row.original.updated_at).toLocaleDateString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
        </span>
      ),
    },
  ]

  return (
    <>
      <div className="px-6 h-10 border-b flex items-center gap-2">
        <Badge variant="secondary">{isLoading ? '…' : total}</Badge>
        <Select
          value={search.stage ?? '__all__'}
          onValueChange={(v) =>
            void navigate({
              search: (prev) => ({
                ...prev,
                stage: v === '__all__' ? undefined : (v as PipelineStage),
              }),
            })
          }
        >
          <SelectTrigger className="h-8 w-[150px]">
            <SelectValue placeholder="All stages" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="__all__">All stages</SelectItem>
            {STAGE_ORDER.map((stage) => (
              <SelectItem key={stage} value={stage}>
                {STAGE_CONFIG[stage].label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <div className="flex-1 overflow-auto">
        <div className="px-6 pt-3 pb-0">
          <DataTable
            columns={columns}
            data={deals}
            loading={isLoading}
            sorting={sorting}
            onSortingChange={handleSortingChange}
            onRowClick={(row) =>
              void navigate({ to: '/pipeline/$id', params: { id: row.id } })
            }
            pagination={{
              pageIndex,
              pageSize: DEFAULT_PAGE_SIZE,
              pageCount: pageCount(total, DEFAULT_PAGE_SIZE),
              totalCount: total,
              onPageChange: setPageIndex,
            }}
            emptyState={
              search.stage ? (
                <EmptyState
                  title="No deals in this stage"
                  description="Nothing is in this stage right now. Change the stage filter to see others."
                />
              ) : (
                <EmptyState
                  title="No pipeline deals yet"
                  description="Your pipeline tracks deals from pre-quote to close. Create a deal from an account, or import existing deals."
                  action={<ImportDealsCta />}
                />
              )
            }
          />
        </div>
      </div>
    </>
  )
}
